import { defineStore } from 'pinia'
import { toDate, isPast, subMinutes, differenceInMilliseconds } from 'date-fns'
import router from '@/router'
import getConfig from '@/utils.js'
import {
  apiGetMyTokens,
  apiPostInitUser,
  apiGetMerchantLinkedWithAccount
} from '@/api/auth.js'
import { useMerchantStore } from './merchant'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    JWT: localStorage.getItem('jwt') || '',
    gmapsApiKey: getConfig().googleGeoApiKey || '',
    user: {},
    refreshAt: null,
    merchantInfo: {},
    currentMerchant: localStorage.getItem('currentMerchant')
      ? JSON.parse(localStorage.getItem('currentMerchant'))
      : { uuid: null, name: 'nope', image: {}, errors: [] }
  }),

  getters: {
    jwt: (state) => state.JWT,

    jwtPayload: (state) =>
      state.JWT ? JSON.parse(atob(state.JWT.split('.')[1])) : null,

    jwtSub() {
      return this.jwtPayload ? this.jwtPayload.sub : null
    },

    jwtIss() {
      return this.jwtPayload ? this.jwtPayload.iss : null
    },

    jwtExp() {
      return this.jwtPayload ? this.jwtPayload.exp : null
    },

    validJWT() {
      return this.jwtExp ? !isPast(toDate(this.jwtExp * 1000)) : false
    },

    isAdmin: (state) => state.user?.level === 'admin',

    isAgency: (state) => state.user?.level === 'agency',

    merchantSearchType: (state) => state.merchantInfo?.searchtype,

    merchantList: (state) => state.merchantInfo?.merchants,

    merchantLevel(state) {
      if (state.user?.level === 'admin') {
        return 10
      } else if (state.user?.level === 'agency') {
        return 5
      }
      if (state.currentMerchant) {
        return state.currentMerchant.level || 0
      }
      return 0
    }
  },

  actions: {
    async initAppAfterLogin() {
      const jwt = this.jwt

      if (!this.refreshAt) {
        const tenMinutesBeforeExpiry = subMinutes(this.jwtExp * 1000, 10)
        this.refreshAt = tenMinutesBeforeExpiry
        const now = new Date()

        setTimeout(async () => {
          const response = await apiGetMyTokens(jwt)
          if (response) {
            const realmName = getConfig().oauthRealm
            for (let i = 0; i < response.tokens.length; i++) {
              const token = response.tokens[i]
              if (token.name === realmName) {
                this.refreshAt = null
                localStorage.setItem('jwt', token.token)
                this.JWT = token.token
                this.initAppAfterLogin()
              }
            }
          }
        }, differenceInMilliseconds(tenMinutesBeforeExpiry, now))
      }

      await this.sendTokenToBackend({ jwt })
      await this.initCurrentMerchant()
    },

    removeToken() {
      localStorage.removeItem('jwt')
      this.JWT = ''
      this.unsetCurrentMerchant()
    },

    async initCurrentMerchant() {
      const currentMerchant = this.currentMerchant

      if (currentMerchant?.uuid) {
        const hasAccess = await apiGetMerchantLinkedWithAccount(
          this.jwt,
          currentMerchant.uuid
        )
        if (hasAccess) {
          return // we have a currentMerchant and are allowed access
        } else {
          this.unsetCurrentMerchant()
        }
      }

      const merchantStore = useMerchantStore()

      switch (this.merchantSearchType) {
        case 'none':
          router.push({ name: 'merchant-create' })
          break
        case 'single':
          this.setCurrentMerchant({ merchant: this.merchantInfo.merchants[0] })
          break
        case 'select':
          merchantStore.showMerchantSelectModal({ show: true, closable: false })
          break
        case 'autocomplete':
          merchantStore.showMerchantSelectModal({ show: true, closable: false })
          break
      }
    },

    setCurrentMerchant({ merchant }) {
      const isSwitching = this.currentMerchant?.uuid !== merchant.uuid
      localStorage.setItem('currentMerchant', JSON.stringify(merchant))
      this.currentMerchant = merchant

      if (isSwitching && router.currentRoute.value.name !== 'dashboard') {
        router.push({ name: 'dashboard' })
      }
    },

    unsetCurrentMerchant() {
      localStorage.removeItem('currentMerchant')
      this.currentMerchant = {
        uuid: null,
        name: 'nope',
        image: {},
        errors: []
      }
    },

    async sendTokenToBackend({ jwt }) {
      const response = await apiPostInitUser(jwt)
      this.user = response.user
      this.merchantInfo = response.merchantinfo
    },

    logout() {
      localStorage.removeItem('jwt')
      this.JWT = ''
      this.user = {}
      this.merchantInfo = {}
      this.unsetCurrentMerchant()
    }
  }
})
